
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/lib/context/auth-context";
import { Link } from "react-router-dom";
import { BosaCertificateRequest } from "./admin/types";

import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { 
  Award, 
  Calendar, 
  Clock, 
  FileCheck,
  FileX, 
  FileQuestion,
  Download,
  ArrowRight,
  AlertCircle,
  CheckCircle,
  XCircle,
  Loader2
} from "lucide-react";
import { formatCEU } from "@/lib/utils";

const BosaRequestStatus = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  
  const {
    data: requests = [],
    isLoading,
    isError,
    refetch
  } = useQuery({
    queryKey: ["user-bosa-requests", user?.id],
    queryFn: async () => {
      try {
        if (!user?.id) return [];
        
        const { data, error } = await supabase
          .from("bosa_certificate_requests")
          .select(`
            *,
            certificates!inner (
              issue_date,
              course_id,
              courses:course_id (
                title,
                hours
              )
            )
          `)
          .eq("user_id", user.id)
          .order("request_date", { ascending: false });

        if (error) {
          throw error;
        }

        // Transform the data to match our type
        return data.map((item: any) => ({
          id: item.id,
          user_id: item.user_id,
          certificate_id: item.certificate_id,
          request_date: item.request_date,
          status: item.status as 'pending' | 'approved' | 'rejected',
          admin_notes: item.admin_notes || undefined,
          user_notes: item.user_notes || undefined,
          approved_date: item.approved_date || undefined,
          rejected_date: item.rejected_date || undefined,
          approved_by: item.approved_by || undefined,
          bosa_certificate_url: item.bosa_certificate_url || undefined,
          created_at: item.created_at,
          updated_at: item.updated_at,
          certificate: {
            course_title: item.certificates?.courses?.title || '',
            course_id: item.certificates?.course_id || '',
            completion_date: item.certificates?.issue_date || '',
            hours: item.certificates?.courses?.hours || 0,
          },
        }));
      } catch (error) {
        console.error("Error fetching BOSA requests:", error);
        toast({
          title: "Error",
          description: "Failed to load your BOSA certificate requests",
          variant: "destructive",
        });
        return [];
      }
    },
    enabled: !!user?.id,
  });

  if (isLoading) {
    return (
      <div className="container mx-auto p-8 space-y-8">
        <Skeleton className="h-10 w-2/3 mb-4" />
        <Skeleton className="h-6 w-1/2 mb-8" />
        
        <div className="grid gap-6 md:grid-cols-2">
          {[1, 2].map((i) => (
            <Card key={i} className="overflow-hidden">
              <CardHeader className="p-6">
                <Skeleton className="h-6 w-3/4 mb-2" />
                <Skeleton className="h-4 w-1/2" />
              </CardHeader>
              <CardContent className="p-6 pt-0">
                <div className="space-y-3">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-4 w-1/2" />
                </div>
              </CardContent>
              <CardFooter className="flex justify-between p-6 pt-0">
                <Skeleton className="h-10 w-24" />
                <Skeleton className="h-10 w-24" />
              </CardFooter>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  // Render statuses with appropriate icons
  const renderStatus = (status: 'pending' | 'approved' | 'rejected') => {
    switch (status) {
      case 'pending':
        return (
          <Badge variant="outline" className="bg-accent-light/20 text-primary border-accent-light">
            <AlertCircle className="mr-1 h-3 w-3" />
            Pending
          </Badge>
        );
      case 'approved':
        return (
          <Badge variant="outline" className="bg-accent/10 text-accent border-accent/30">
            <CheckCircle className="mr-1 h-3 w-3" />
            Approved
          </Badge>
        );
      case 'rejected':
        return (
          <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
            <XCircle className="mr-1 h-3 w-3" />
            Rejected
          </Badge>
        );
      default:
        return (
          <Badge variant="outline">Unknown</Badge>
        );
    }
  };

  const downloadCertificate = async (url: string) => {
    // Open the certificate URL in a new tab
    window.open(url, '_blank');
  };

  return (
    <div className="container mx-auto p-8 space-y-8 animate-fade-in">
      <div>
        <h1 className="text-3xl font-title font-bold text-primary flex items-center gap-3">
          <Award className="h-8 w-8 text-primary" />
          My BOSA Certificate Requests
        </h1>
        <p className="text-muted-foreground mt-2 font-subtitle">
          Track the status of your BOSA certificate requests
        </p>
      </div>

      {requests.length === 0 ? (
        <Card className="p-8 text-center border-secondary/20 shadow-md">
          <div className="flex flex-col items-center justify-center space-y-4">
            <FileQuestion className="h-16 w-16 text-secondary opacity-50" />
            <div>
              <h3 className="text-lg font-semibold font-subtitle text-primary">No BOSA certificate requests found</h3>
              <p className="text-muted-foreground font-body mt-2">
                You haven't requested any BOSA certificates yet. After completing a course, you can request a BOSA certificate.
              </p>
            </div>
            <Button asChild className="mt-4 bg-primary hover:bg-primary/90">
              <Link to="/bosa-request">
                Request BOSA Certificate
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </Card>
      ) : (
        <div className="grid gap-6 md:grid-cols-2">
          {requests.map((request) => (
            <Card key={request.id} className="overflow-hidden border-secondary/20 hover:border-secondary/40 transition-all duration-300 shadow-md">
              <CardHeader className="bg-gradient-to-r from-white to-accent-subtle/30">
                <div className="flex justify-between items-start">
                  <div className="space-y-1">
                    <CardTitle className="font-title text-lg text-primary">
                      {request.certificate.course_title}
                    </CardTitle>
                    <CardDescription className="font-caption">
                      Requested on {new Date(request.request_date).toLocaleDateString()}
                    </CardDescription>
                  </div>
                  {renderStatus(request.status)}
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex flex-col space-y-1">
                    <div className="text-sm flex items-center text-primary/70">
                      <Clock className="h-4 w-4 mr-2 text-accent" />
                      <span className="font-body">{formatCEU(request.certificate.hours)} CEUs</span>
                    </div>
                    <div className="text-sm flex items-center text-primary/70">
                      <Calendar className="h-4 w-4 mr-2 text-accent" />
                      <span className="font-body">
                        Completed: {new Date(request.certificate.completion_date).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  
                  {request.status === 'approved' && (
                    <div className="mt-2 p-3 bg-accent/10 border border-accent/20 rounded-md">
                      <p className="text-sm text-accent font-body">
                        Your BOSA certificate has been approved! You can download it using the button below.
                      </p>
                    </div>
                  )}
                  
                  {request.status === 'rejected' && request.admin_notes && (
                    <div className="mt-2 p-3 bg-red-50 border border-red-100 rounded-md">
                      <p className="text-sm text-red-700 font-body mb-1 font-medium">
                        Your request was not approved.
                      </p>
                      <p className="text-sm text-red-700 font-body">
                        Reason: {request.admin_notes}
                      </p>
                    </div>
                  )}
                  
                  {request.status === 'pending' && (
                    <div className="mt-2 p-3 bg-accent-light/10 border border-accent-light/20 rounded-md">
                      <p className="text-sm text-primary font-body">
                        Your request is being reviewed. You'll be notified when it's processed.
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
              <CardFooter className="flex justify-between border-t bg-muted/20 px-6 py-4">
                {request.status === 'approved' && request.bosa_certificate_url ? (
                  <Button 
                    variant="default" 
                    className="w-full bg-accent hover:bg-accent/90 text-white" 
                    onClick={() => downloadCertificate(request.bosa_certificate_url!)}
                  >
                    <Download className="mr-2 h-4 w-4" />
                    Download BOSA Certificate
                  </Button>
                ) : (
                  <Button 
                    variant={request.status === 'rejected' ? 'outline' : 'default'}
                    className={`w-full ${request.status === 'rejected' ? 'border-accent text-primary hover:bg-accent-subtle' : 'bg-accent-light hover:bg-accent-light/90 text-primary'}`}
                    disabled={request.status === 'pending'}
                    asChild
                  >
                    {request.status === 'rejected' ? (
                      <Link to="/bosa-request">
                        <FileCheck className="mr-2 h-4 w-4" />
                        Submit New Request
                      </Link>
                    ) : (
                      <span>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Awaiting Approval
                      </span>
                    )}
                  </Button>
                )}
              </CardFooter>
            </Card>
          ))}
        </div>
      )}
      
      {requests.length > 0 && (
        <div className="flex justify-center mt-8">
          <Button variant="outline" asChild className="border-primary/30 text-primary hover:bg-accent-subtle">
            <Link to="/bosa-request">
              Request Another BOSA Certificate
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>
      )}
    </div>
  );
};

export default BosaRequestStatus;
